import {
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Icon } from '@chakra-ui/react';

interface SummaryOverviewProps {
  summary: {
    rocksCreated: number;
    rocksCompleted: number;
    rocksDropped: number;
    issuesCreated: number;
    issuesResolved: number;
    actionItemsCreated: number;
    actionItemsCompleted: number;
    metricsCreated: number;
    winsExcitedCount: number;
  };
  trendData?: {
    percentageChanges: {
      rocksCreated: number;
      rocksCompleted: number;
      issuesCreated: number;
      issuesResolved: number;
      actionItemsCreated: number;
      actionItemsCompleted: number;
    };
    actionItemCompletionRate: number;
  } | null;
}

/**
 * Summary Overview Component
 * Displays key statistics in a grid layout
 */
export function SummaryOverview({ summary, trendData }: SummaryOverviewProps) {
  const statBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const getTrendIcon = (change: number) => {
    if (change > 0) return <Icon as={TrendingUp} w={4} h={4} color="green.500" />;
    if (change < 0) return <Icon as={TrendingDown} w={4} h={4} color="red.500" />;
    return null;
  };

  const formatChange = (change: number) => {
    if (change === 0) return 'No change';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change}%`;
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
      {/* Rocks Created */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Rocks Created</StatLabel>
        <StatNumber>{summary.rocksCreated}</StatNumber>
        {trendData && (
          <StatHelpText>
            <Box display="flex" alignItems="center" gap={1}>
              {getTrendIcon(trendData.percentageChanges.rocksCreated)}
              {formatChange(trendData.percentageChanges.rocksCreated)}
            </Box>
          </StatHelpText>
        )}
      </Stat>

      {/* Rocks Completed */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Rocks Completed</StatLabel>
        <StatNumber color="green.500">{summary.rocksCompleted}</StatNumber>
        {trendData && (
          <StatHelpText>
            <Box display="flex" alignItems="center" gap={1}>
              {getTrendIcon(trendData.percentageChanges.rocksCompleted)}
              {formatChange(trendData.percentageChanges.rocksCompleted)}
            </Box>
          </StatHelpText>
        )}
      </Stat>

      {/* Rocks Dropped */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Rocks Dropped</StatLabel>
        <StatNumber color="red.500">{summary.rocksDropped}</StatNumber>
      </Stat>

      {/* Issues Created */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Issues Created</StatLabel>
        <StatNumber>{summary.issuesCreated}</StatNumber>
        {trendData && (
          <StatHelpText>
            <Box display="flex" alignItems="center" gap={1}>
              {getTrendIcon(trendData.percentageChanges.issuesCreated)}
              {formatChange(trendData.percentageChanges.issuesCreated)}
            </Box>
          </StatHelpText>
        )}
      </Stat>

      {/* Issues Resolved */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Issues Resolved</StatLabel>
        <StatNumber color="green.500">{summary.issuesResolved}</StatNumber>
        {trendData && (
          <StatHelpText>
            <Box display="flex" alignItems="center" gap={1}>
              {getTrendIcon(trendData.percentageChanges.issuesResolved)}
              {formatChange(trendData.percentageChanges.issuesResolved)}
            </Box>
          </StatHelpText>
        )}
      </Stat>

      {/* Action Items Created */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Action Items Created</StatLabel>
        <StatNumber>{summary.actionItemsCreated}</StatNumber>
        {trendData && (
          <StatHelpText>
            <Box display="flex" alignItems="center" gap={1}>
              {getTrendIcon(trendData.percentageChanges.actionItemsCreated)}
              {formatChange(trendData.percentageChanges.actionItemsCreated)}
            </Box>
          </StatHelpText>
        )}
      </Stat>

      {/* Action Items Completed */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Action Items Completed</StatLabel>
        <StatNumber color="green.500">{summary.actionItemsCompleted}</StatNumber>
        {trendData && (
          <StatHelpText>
            <Box display="flex" alignItems="center" gap={1}>
              {getTrendIcon(trendData.percentageChanges.actionItemsCompleted)}
              {formatChange(trendData.percentageChanges.actionItemsCompleted)}
            </Box>
            {trendData.actionItemCompletionRate > 0 && (
              <StatHelpText>
                Completion Rate: {trendData.actionItemCompletionRate}%
              </StatHelpText>
            )}
          </StatHelpText>
        )}
      </Stat>

      {/* Metrics Created */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Metrics Created</StatLabel>
        <StatNumber>{summary.metricsCreated}</StatNumber>
      </Stat>

      {/* Wins & Excited */}
      <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <StatLabel>Wins & Excited</StatLabel>
        <StatNumber color="purple.500">{summary.winsExcitedCount}</StatNumber>
      </Stat>
    </SimpleGrid>
  );
}

